// A named blueprint under Workspaces · Blueprints (02-DESIGN/03-console/01-the-console.md
// § the blueprint's page; 02-DESIGN/13-blueprints/01-the-blueprint.md
// § named blueprints, § the console: Workspaces; decision 0052): the
// blueprint editor in tabs — one per section of the document, in its order,
// opening on *Sign-in*, the grants on *Limits*, and *History* — each holding
// its section's form in the design's words. The document is saved whole
// under the revision guard, so *Save* is the page's action on every tab; a
// tab with unsaved changes carries a dot; *Save* names how many workspaces
// the change reaches and asks first with what it does there, section by
// section; leaving with unsaved changes asks first. Its secrets are slots
// each workspace fills on its own page. *History* lists the versions, each
// opening its page, `workspaces/blueprints/<name>/history/<revision>`, with
// *Restore this version* (BlueprintRevisionPage.tsx). *Delete* is the last
// entry of the page's menu, only while no workspace uses it. A name the
// library does not hold yet opens an empty editor, and the first save
// creates it.

import { blueprint, identity } from "@impire-io/sdk";
import { useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { CODE_NO_SERVICE, isRefusal } from "../../platform/bus.ts";
import { usePage } from "../../platform/session.tsx";
import {
  Button,
  describe,
  Empty,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  plural,
  useLoad,
  when,
} from "../../ui/components.tsx";
import { ConfirmDialog, RowMenu, Table, type Column, type MenuEntry } from "../../ui/table.tsx";
import { Tabs, useTab } from "../../ui/tabs.tsx";
import {
  Code,
  EXPECT_REVISION_HEADER,
  SERVICE,
  Subjects,
  validName,
  type ChildrenReply,
  type Entry,
  type HistoryReply,
} from "../../wire/blueprint.ts";
import { DOCUMENT_SETTINGS } from "../../wire/config.ts";
import {
  SERVICE as SOLUTIONS,
  Subjects as SolutionSubjects,
  type CatalogueListReply,
} from "../../wire/solutions.ts";
import {
  listSubject,
  RegistrationState,
  SERVICE as WORKLOADS,
  type ListReply,
} from "../../wire/workloads.ts";
import { useSurface } from "../surface.tsx";
import {
  BLUEPRINT_TABS,
  changedSections,
  effectsBySection,
  SECTIONS,
  slotEffects,
  TAB_OF_SECTION,
  tabOf,
} from "./blueprint.ts";
import {
  LimitsSection,
  RequiredSection,
  RulesSection,
  SignInSection,
  SlotsSection,
  SolutionsSection,
  WorkloadsSection,
  type OwnWorkload,
} from "./BlueprintForms.tsx";
import { fromOwn, libraryDocumentOf, libraryDraftOf, sameValue, type Draft } from "./forms.ts";
import { NameLink } from "../../ui/ident.tsx";
import { BLUEPRINTS_TAB, childPath, libraryOf, revisionPath, saidOf, settingsOf } from "./library.ts";
import { useAskBeforeLeaving } from "./leaving.tsx";

export function NamedBlueprintPage() {
  const { name = "" } = useParams();
  return <Named key={name} name={name} />;
}

/** absent reads a not-found refusal as absence. */
function absent<T>(read: Promise<T>): Promise<T | undefined> {
  return read.catch((err: unknown) => {
    if (isRefusal(err, Code.notFound)) {
      return undefined;
    }
    throw err;
  });
}

function Named({ name }: { name: string }) {
  const s = useSurface();
  const location = useLocation();
  const [tick, setTick] = useState(0);
  // What a revision's page said it restored, carried here in the navigation state.
  const [saved, setSaved] = useState<string | undefined>(() => saidOf(location.state));
  const reload = () => {
    setTick((t) => t + 1);
  };
  const ok = validName(name);
  const [doc] = useLoad(
    `library:${name}`,
    () =>
      ok ? absent(s.bus.call<Entry>(SERVICE, Subjects.libraryGet(name), {})) : Promise.resolve(undefined),
    tick,
  );
  const [history] = useLoad(
    `library-history:${name}`,
    () =>
      ok
        ? absent(s.bus.call<HistoryReply>(SERVICE, Subjects.libraryHistory(name), undefined))
        : Promise.resolve(undefined),
    tick,
  );
  const [rows] = useLoad(
    "blueprint-children",
    () => s.bus.call<ChildrenReply>(SERVICE, Subjects.children(), undefined),
    tick,
  );
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "workspaces")}>Workspaces</Link>
      <span>/</span>
      <Link to={tenantPath(s.tenant, "workspaces")} state={{ tab: BLUEPRINTS_TAB }}>
        Blueprints
      </Link>
      <span>/</span>
      <span className="mono">{name}</span>
    </>
  );
  if (!ok) {
    return (
      <>
        <PageHead crumb={crumb} title={name} />
        <Empty>
          A blueprint&apos;s name is letters, digits, hyphens and underscores: &ldquo;{name}&rdquo; is none.
        </Empty>
      </>
    );
  }
  if (doc.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading the blueprint" />
      </>
    );
  }
  if (doc.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title={name} />
        <NotAllowed err={doc.err} what="This blueprint" />
      </>
    );
  }
  const entry = doc.value?.op === blueprint.EntryOp.put ? doc.value : undefined;
  const users = (rows.state === "ready" ? (rows.value.children ?? []) : [])
    .filter((r) => r.blueprint === name)
    .map((u) => ({ slug: u.slug, name: u.name ?? u.slug }));
  return (
    <Editor
      key={entry?.revision ?? 0}
      crumb={crumb}
      name={name}
      entry={entry}
      users={users}
      history={history.state === "ready" ? (history.value?.entries ?? []) : []}
      saved={saved}
      onSaved={(said) => {
        setSaved(said);
        reload();
      }}
    />
  );
}

/** usedBy is the line under the title: how many workspaces use it, each name a link to its page. */
function usedBy(tenant: string, users: readonly { slug: string; name: string }[]): ReactNode {
  if (users.length === 0) {
    return "Used by no workspace yet: saving changes nothing anywhere until you apply it to one.";
  }
  return (
    <>
      Used by {plural(users.length, "workspace")}:{" "}
      {users.map((u, i) => (
        <span key={u.slug}>
          {i > 0 && ", "}
          <NameLink to={childPath(tenant, u.slug)}>{u.name}</NameLink>
        </span>
      ))}
    </>
  );
}

function Editor({
  crumb,
  name,
  entry,
  users,
  history,
  saved,
  onSaved,
}: {
  crumb: ReactNode;
  name: string;
  entry: Entry | undefined;
  /** The workspaces that use it: who a change reaches. */
  users: readonly { slug: string; name: string }[];
  history: Entry[];
  saved: string | undefined;
  onSaved: (said?: string) => void;
}) {
  const s = useSurface();
  const navigate = useNavigate();
  const location = useLocation();
  const { platform } = usePage();
  const before = libraryOf(entry);
  const [draft, setDraft] = useState<Draft>(() => libraryDraftOf(before));
  // A tab is a view of the page and not in the address; a child's section
  // line asks for its tab in the link's navigation state, `{ tab }`.
  const [tab, setTab] = useTab(BLUEPRINT_TABS, tabOf(location.state));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const [asking, setAsking] = useState(false);

  // The pick lists: this workspace's own models and toolkits, to start a
  // required one from, and the catalogue, for the solutions and the limits.
  const [workloads] = useLoad("blueprint/own-workloads", () =>
    s.bus.call<ListReply>(WORKLOADS, listSubject(), undefined),
  );
  const [catalogue] = useLoad("blueprint/catalogue", () =>
    s.bus.call<CatalogueListReply>(SOLUTIONS, SolutionSubjects.catalogueList(), undefined),
  );
  const inferenceImage = platform.images?.[identity.Image.inference] || undefined;
  const toolkitImage = platform.images?.[identity.Image.toolkitMCP] || undefined;
  const own: OwnWorkload[] =
    workloads.state === "ready"
      ? workloads.value.workloads
          .filter(
            (w) =>
              w.state !== RegistrationState.retired && w.platform === undefined && w.solution === undefined,
          )
          .flatMap((w): OwnWorkload[] => {
            const runs = (x: string | undefined) => x !== undefined && (w.image === x || w.digest === x);
            return runs(inferenceImage)
              ? [{ id: w.id, name: w.name, kind: "model" }]
              : runs(toolkitImage)
                ? [{ id: w.id, name: w.name, kind: "toolkit" }]
                : [];
          })
      : [];
  const entries = catalogue.state === "ready" ? catalogue.value.solutions : [];
  const manifests = new Map(entries.map((e) => [e.latest.name, e.latest.manifest.settings ?? []]));
  const catalogueNote =
    catalogue.state === "refused"
      ? isRefusal(catalogue.err, CODE_NO_SERVICE)
        ? "Nothing serves the catalogue on this install yet, so no solution can be picked."
        : `The catalogue cannot be read: ${describe(catalogue.err)}`
      : catalogue.state === "pending"
        ? "Reading the catalogue…"
        : undefined;

  const { document: after, errors } = libraryDocumentOf(draft);
  const valid = Object.keys(errors).length === 0;
  const changed = entry === undefined || !sameValue(before ?? {}, after);
  const dirty = changedSections(before, after);
  const changedTabs = new Set([...dirty].map((k) => TAB_OF_SECTION[k]));
  const savedHandles = new Set((before?.workloads ?? []).map((w) => w.handle));
  const leaving = useAskBeforeLeaving(
    dirty.size > 0 && !busy,
    `What you changed on ${name} and have not saved is dropped.`,
  );

  const copy = (o: OwnWorkload) => {
    setErr(undefined);
    const w = workloads.state === "ready" ? workloads.value.workloads.find((x) => x.id === o.id) : undefined;
    if (w === undefined) {
      return;
    }
    void s.bus
      .configuration()
      .get(o.id, DOCUMENT_SETTINGS)
      .then(
        (e) => e.value,
        () => undefined,
      )
      .then((text) => {
        setDraft((d) => {
          const made = fromOwn(
            w,
            o.kind,
            settingsOf(text),
            d.workloads.map((x) => x.handle),
          );
          return {
            ...d,
            workloads: [...d.workloads, made.workload],
            required: made.required === undefined ? d.required : [...d.required, made.required],
          };
        });
      });
  };

  const save = () => {
    setAsking(false);
    if (busy || !valid || !changed) {
      return;
    }
    setBusy(true);
    setErr(undefined);
    // A new name is created, never written over one that landed meanwhile.
    const extra = { [EXPECT_REVISION_HEADER]: String(entry?.revision ?? 0) };
    s.bus.callText<Entry>(SERVICE, Subjects.librarySet(name), JSON.stringify(after, null, 2), extra).then(
      (e) => {
        setBusy(false);
        const n = e.children ?? 0;
        onSaved(
          n === 0
            ? `Saved as version ${String(e.revision)}.`
            : `Saved as version ${String(e.revision)}. ${plural(n, "workspace")} using it follow; each one's steward takes it at its next pass.`,
        );
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  const remove = () => {
    setErr(undefined);
    s.bus.call<Entry>(SERVICE, Subjects.libraryDelete(name), undefined).then(
      () => {
        void navigate(tenantPath(s.tenant, "workspaces"), { state: { tab: BLUEPRINTS_TAB } });
      },
      (e: unknown) => {
        setErr(e);
      },
    );
  };

  const common = { draft, setDraft };
  const child = users.length === 1 ? (users[0]?.name ?? "the workspace") : "each workspace";
  const wrong = SECTIONS.filter((x) => errors[x.key] !== undefined).map((x) => x.title);
  // What a save does in each workspace that uses it, section by section: the
  // secrets as slots, since each workspace holds its own values.
  const effects = [
    ...effectsBySection(before === undefined ? undefined : { ...before, secrets: undefined }, {
      ...after,
      secrets: undefined,
    }),
  ];
  const slots = slotEffects(before?.secrets, after.secrets);
  if (slots.length > 0) {
    const at = effects.findIndex((e) => e.key === "limits");
    effects.splice(at === -1 ? effects.length : at, 0, { key: "secrets", title: "Secrets", lines: slots });
  }
  const reaches = users.length > 0 && changed;

  const menu: MenuEntry[] = [
    ...(entry !== undefined && changed
      ? [
          {
            label: "Discard changes",
            onSelect: () => {
              setDraft(libraryDraftOf(before));
              setErr(undefined);
            },
            confirm: {
              title: "Discard your changes?",
              consequence: `Every tab returns to version ${String(entry.revision)} as it was saved.`,
              verb: "Discard",
            },
          },
        ]
      : []),
    ...(entry !== undefined && users.length === 0
      ? [
          {
            label: "Delete",
            danger: true as const,
            onSelect: remove,
            confirm: {
              title: `Delete blueprint ${name}?`,
              consequence:
                "It is removed from your blueprints. No workspace uses it, so nothing changes anywhere.",
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHead
        crumb={crumb}
        title={name}
        line={
          entry === undefined
            ? "A new blueprint: nothing is saved yet. Save it, then apply it to a workspace."
            : usedBy(s.tenant, users)
        }
        action={
          <>
            <RowMenu label={`Actions for ${name}`} entries={menu} />
            <Button
              kind="primary"
              disabled={busy || !valid || !changed}
              onClick={() => {
                if (reaches) {
                  setAsking(true);
                } else {
                  save();
                }
              }}
            >
              {busy ? "Saving…" : reaches ? `Save, changes ${plural(users.length, "workspace")}` : "Save"}
            </Button>
          </>
        }
      />
      {saved !== undefined && !changed && <Notice kind="ok">{saved}</Notice>}
      {!valid && (
        <Notice kind="bad" title="Not saved yet">
          Fix what {list(wrong)} says before saving.
        </Notice>
      )}
      {err !== undefined &&
        (isRefusal(err, Code.conflict) ? (
          <Notice kind="bad" title="Someone saved this blueprint after you opened it">
            Nothing of yours was saved. Reload to see their version, then make your change again; reloading
            drops what you changed here.{" "}
            <button
              type="button"
              className="text-link"
              onClick={() => {
                onSaved();
              }}
            >
              Reload
            </button>
          </Notice>
        ) : isRefusal(err, Code.inUse) ? (
          <Notice kind="bad" title="Still in use">
            A blueprint a workspace uses cannot be deleted. Change those workspaces to another blueprint
            first.
          </Notice>
        ) : isRefusal(err, Code.limit) ? (
          <Notice kind="bad" title="The library is full">
            {s.name} keeps at most {blueprint.MAX_LIBRARY} blueprints. Delete one no workspace uses first.
          </Notice>
        ) : (
          <NotAllowed err={err} what="Save" />
        ))}
      <Tabs
        label={name}
        tabs={BLUEPRINT_TABS.map((t) => ({ ...t, changed: changedTabs.has(t.id) }))}
        value={tab}
        onChange={setTab}
      >
        {tab === "sign-in" ? (
          <SignInSection {...common} error={errors["sign-in"]} child={child} />
        ) : tab === "required-settings" ? (
          <RequiredSection {...common} error={errors.required} manifests={manifests} />
        ) : tab === "guardrails" ? (
          <RulesSection {...common} error={errors.rules} child={child} />
        ) : tab === "workloads" ? (
          <WorkloadsSection
            {...common}
            error={errors.workloads}
            saved={savedHandles}
            own={own}
            ownName={s.name}
            onCopy={copy}
          />
        ) : tab === "solutions" ? (
          <SolutionsSection
            {...common}
            error={errors.solutions}
            catalogue={entries}
            catalogueNote={catalogueNote}
          />
        ) : tab === "secrets" ? (
          <SlotsSection {...common} error={errors.secrets} />
        ) : tab === "limits" ? (
          <LimitsSection {...common} error={errors.limits} catalogue={entries} />
        ) : (
          <History name={name} current={entry} entries={history} />
        )}
      </Tabs>
      {asking && (
        <ConfirmDialog
          ask={{
            title: `This changes ${plural(users.length, "workspace")}`,
            consequence: `${users.map((u) => u.name).join(", ")}: each one's steward takes the new version at its next pass.`,
          }}
          verb="Save"
          danger={false}
          onConfirm={save}
          onCancel={() => {
            setAsking(false);
          }}
        >
          {effects.every((e) => e.lines.length === 0) ? (
            <p className="small muted">Nothing it does not already do there.</p>
          ) : (
            effects
              .filter((e) => e.lines.length > 0)
              .map((e) => (
                <div key={e.key}>
                  <h3>{e.title}</h3>
                  <ul className="small">
                    {e.lines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
              ))
          )}
        </ConfirmDialog>
      )}
      {leaving}
    </>
  );
}

/** list joins tab names the way a sentence does: *Workloads and Limits*. */
function list(xs: readonly string[]): string {
  if (xs.length <= 1) {
    return xs[0] ?? "a tab";
  }
  return `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1] ?? ""}`;
}

/**
 * History is the blueprint's versions, newest first, as a table; a version
 * opens its page, which says what restoring it does in each workspace using
 * it, with *Restore this version*.
 */
function History({ name, current, entries }: { name: string; current: Entry | undefined; entries: Entry[] }) {
  const s = useSurface();
  const columns: Column<Entry>[] = [
    {
      key: "version",
      header: "Version",
      width: "120px",
      value: (e) => e.revision,
      cell: (e) => (
        <span className="cell-main">
          version {e.revision}
          {e.revision === current?.revision && <span className="small faint"> · current</span>}
        </span>
      ),
    },
    {
      key: "what",
      header: "What",
      width: "120px",
      value: (e) => (e.op === blueprint.EntryOp.delete ? "deleted" : "saved"),
      facet: "What",
      cell: (e) => (e.op === blueprint.EntryOp.delete ? "deleted" : "saved"),
    },
    {
      key: "by",
      header: "By",
      value: (e) => e.author ?? "",
      cell: (e) => <span className="muted">{e.author ?? ""}</span>,
      narrow: false,
    },
    {
      key: "when",
      header: "When",
      width: "180px",
      value: (e) => e.created ?? "",
      cell: (e) => <span className="muted">{e.created !== undefined ? when(e.created) : ""}</span>,
    },
  ];
  return entries.length === 0 ? (
    <Empty>No earlier version: saving it makes the first.</Empty>
  ) : (
    <Table
      label="Versions"
      rows={entries}
      columns={columns}
      rowKey={(e) => String(e.revision)}
      sort={{ key: "version", dir: "desc" }}
      to={(e) => revisionPath(s.tenant, name, e.revision)}
    />
  );
}
